import { useContext, useLayoutEffect, useRef } from 'react';
import { DayButtonProps, DayPicker } from 'react-day-picker';

import { getYear } from 'date-fns';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { dateFnsLocales } from '@utils/date';
import { transitionFast } from '@utils/transitions';

import { ArrowNavContext } from './arrow-nav';
import { CalendarHeader } from './calendar-header';
import { MonthGrid, YearGrid } from './grids';
import { DAY_CELL_PX, disabledCellStyle, interactiveCellStyle, selectedCellStyle } from './styles';
import { CalendarView, LanguageOptions } from './types';

/**
 * base-ui restores focus to the popup root before react-day-picker's default
 * DayButton effect can focus the next day after month-boundary keyboard nav;
 * focusing from a layout effect wins that race and keeps arrow-key nav alive.
 *
 * In the popover (arrowNav), `tabIndex={-1}` overrides RDP's roving tab stop so
 * the day grid is out of the page tab order — Tab skips the calendar and focus
 * enters via the arrow keys. RDP still drives day-to-day movement off
 * `modifiers.focused`, so dropping the tab stop doesn't break in-grid nav.
 * Inline calendars keep RDP's default tabbing.
 */
const SyncFocusDayButton = ({ day, modifiers, ...buttonProps }: DayButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const arrowNav = useContext(ArrowNavContext);
  useLayoutEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  return (
    <button
      ref={ref}
      data-testid="datepicker-day"
      // Per-day e2e handle (e.g. datepicker-day-15). Only on in-month days so
      // the day number stays a unique match — showOutsideDays repeats low/high
      // numbers from the adjacent months.
      data-cy={modifiers.outside ? undefined : `datepicker-day-${day.date.getDate()}`}
      data-outside={modifiers.outside || undefined}
      aria-current={modifiers.today ? 'date' : undefined}
      {...buttonProps}
      {...(arrowNav ? { tabIndex: -1 } : {})}
    />
  );
};

type CalendarBodyProps = {
  view: CalendarView;
  viewingDate: Date;
  dayPickerKey: number;
  selected: Date | undefined;
  lang: LanguageOptions;
  minDate?: Date;
  maxDate?: Date;
  // True when rendered inside the popover (arrow-key entry + Tab trap, controls
  // out of the page tab order); false for an inline calendar.
  arrowNavMode: boolean;
  onDaySelect: (date: Date | undefined, triggerDate: Date) => void;
  onViewSelect: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onViewDateChange: (date: Date) => void;
  onHeaderViewDateChange: (date: Date) => void;
};

export const CalendarBody = (props: CalendarBodyProps) => {
  const {
    view,
    viewingDate,
    dayPickerKey,
    selected,
    lang,
    minDate,
    maxDate,
    arrowNavMode,
    onDaySelect,
    onViewSelect,
    onViewChange,
    onViewDateChange,
    onHeaderViewDateChange,
  } = props;

  return (
    <ArrowNavContext.Provider value={arrowNavMode}>
      <CalendarHeader
        view={view}
        date={viewingDate}
        lang={lang}
        onViewChange={onViewChange}
        onViewDateChange={onHeaderViewDateChange}
      />
      {view === 'day' && (
        <DayPickerWrapper>
          <DayPicker
            // `key` + `defaultMonth` (not `month`) keep RDP in uncontrolled month
            // mode so keyboard nav across month boundaries survives (see
            // dayPickerKey in datepicker.tsx). `DayButton` is a focus-timing
            // workaround; see SyncFocusDayButton.
            key={dayPickerKey}
            mode="single"
            selected={selected}
            onSelect={onDaySelect}
            defaultMonth={viewingDate}
            onMonthChange={onViewDateChange}
            locale={dateFnsLocales[lang]}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            components={{ DayButton: SyncFocusDayButton }}
            hideNavigation
            showOutsideDays
            // Always render 6 week-rows so the calendar height is constant
            // across months. Otherwise a 5↔6 row change resizes the inline
            // calendar, and the floating Menu hosting it (form-builder date
            // condition) re-runs flip/moveUp positioning and visibly jumps.
            fixedWeeks
          />
        </DayPickerWrapper>
      )}
      {view === 'month' && (
        <MonthGrid
          year={getYear(viewingDate)}
          selected={selected}
          lang={lang}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={onViewSelect}
        />
      )}
      {view === 'year' && (
        <YearGrid
          anchorYear={getYear(viewingDate)}
          selected={selected}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={onViewSelect}
        />
      )}
    </ArrowNavContext.Provider>
  );
};

const DayPickerWrapper = styled.div({
  width: '100%',
  // We render our own header in CalendarHeader; hide react-day-picker's.
  '.rdp-month_caption': {
    display: 'none',
  },
  '.rdp-month_grid': {
    width: '100%',
    borderCollapse: 'collapse',
  },
  '.rdp-weekdays': {
    display: 'flex',
  },
  '.rdp-weekday': {
    width: pxToRem(DAY_CELL_PX),
    height: pxToRem(DAY_CELL_PX),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    font: theme.font.semibold12,
    color: theme.color.textSecondary,
    textTransform: 'none',
  },
  '.rdp-weeks': {
    display: 'flex',
    flexDirection: 'column',
  },
  '.rdp-week': {
    display: 'flex',
  },
  '.rdp-day': {
    padding: 0,
    height: pxToRem(DAY_CELL_PX),
    width: pxToRem(DAY_CELL_PX),
  },
  '.rdp-day_button': {
    width: '100%',
    height: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    font: theme.font.regular14,
    color: theme.color.textPrimary,
    borderRadius: pxToRem(8),
    cursor: 'pointer',
    outline: 'none',
    transition: transitionFast('background-color'),
    ...interactiveCellStyle,
  },
  '.rdp-outside .rdp-day_button': {
    color: theme.color.textHelper,
  },
  '.rdp-today .rdp-day_button': {
    color: theme.color.elementBlue100,
    font: theme.font.semibold14,
  },
  // When no date is selected, paint today as the default highlighted day.
  '.rdp-month_grid:not(:has(.rdp-selected)) .rdp-today .rdp-day_button': selectedCellStyle,
  '.rdp-selected .rdp-day_button': selectedCellStyle,
  '.rdp-disabled .rdp-day_button': disabledCellStyle,
});
